import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  FormLabel,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Divider,
  Input,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { AddIcon, CheckIcon, CloseIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { getBackendURL, basename } from "./env.js";
import { getSupabase } from "./supabaseClient";

const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const formatTime = (t) => {
  if (!t) return "";
  const [hh, mm] = t.split(":");
  const h = parseInt(hh, 10);
  if (Number.isNaN(h)) return t;
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${mm} ${period}`;
};

const TIME_OPTIONS = (() => {
  const out = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      out.push({ value, label: formatTime(value) });
    }
  }
  return out;
})();

const newSlot = () => ({ day_of_week: "", start_time: "09:00", end_time: "10:00" });

const csrf = () =>
  document.cookie.split("csrftoken=")[1]?.split(";")[0] || "";

const authHeaders = async () => {
  try {
    const supabase = await getSupabase();
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch (_) {
    return {};
  }
};

/**
 * Modal that lets a SuperAdmin add and remove weekly recurring availability.
 * Each rule = (day-of-week × start_time × end_time) — applies every week.
 * To express "Tuesdays 2–4pm AND Thursdays 1–3pm" the admin adds 2 slot rows
 * before saving.
 */
const MeetingAvailabilityModal = ({ isOpen, onClose }) => {
  const toast = useToast();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  const [slots, setSlots] = useState([newSlot()]);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [editingDraft, setEditingDraft] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  // Personal public booking link (null = not generated yet).
  const [bookingSlug, setBookingSlug] = useState(null);
  const [linkBusy, setLinkBusy] = useState(false);

  const bookingUrl = bookingSlug
    ? `${window.location.origin}${basename || ""}/book/${bookingSlug}`
    : "";

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getBackendURL()}/api/meeting-availability/`, {
        credentials: "include",
        headers,
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || `HTTP ${res.status}`);
      setRules(j.rules || []);
      setCurrentUserId(j.current_user_id || null);
    } catch (err) {
      toast({
        title: "Could not load availability",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Read-only: never mints a slug, so merely opening the modal doesn't
  // publish a booking page. Generation is an explicit click.
  const fetchBookingLink = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getBackendURL()}/api/meeting-bookings/link/`, {
        credentials: "include",
        headers,
      });
      if (!res.ok) return;
      const j = await res.json();
      setBookingSlug(j.slug || null);
    } catch (_) {
      /* link section just stays in the "not generated" state */
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchRules();
      fetchBookingLink();
      setSlots([newSlot()]);
      setEditingRuleId(null);
      setEditingDraft(null);
    }
  }, [isOpen, fetchRules, fetchBookingLink]);

  const handleGenerateLink = async ({ regenerate = false } = {}) => {
    if (regenerate) {
      const ok = window.confirm(
        "Generate a new link? Anyone holding the current link will no longer be able to book with you.",
      );
      if (!ok) return;
    }
    setLinkBusy(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getBackendURL()}/api/meeting-bookings/link/`, {
        method: "POST",
        credentials: "include",
        headers: {
          ...headers,
          "Content-Type": "application/json",
          "X-CSRFToken": csrf(),
        },
        body: JSON.stringify({ regenerate }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || `HTTP ${res.status}`);
      setBookingSlug(j.slug);
      toast({
        title: j.regenerated ? "New link generated" : "Booking link ready",
        description: j.regenerated ? "The previous link no longer works." : undefined,
        status: "success",
        duration: 4000,
      });
    } catch (err) {
      toast({
        title: "Could not generate link",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setLinkBusy(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(bookingUrl);
      toast({ title: "Link copied", status: "success", duration: 2000 });
    } catch (_) {
      // clipboard is blocked on insecure origins — the input is selectable
      toast({
        title: "Copy failed — select the link and copy manually",
        status: "warning",
        duration: 4000,
      });
    }
  };

  const handleAddSlot = () => setSlots((prev) => [...prev, newSlot()]);
  const handleRemoveSlot = (idx) =>
    setSlots((prev) => prev.filter((_, i) => i !== idx));
  const handleSlotChange = (idx, field, value) =>
    setSlots((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));

  const handleSave = async () => {
    if (!slots.length) {
      toast({ title: "Add at least one time slot", status: "error", duration: 3000 });
      return;
    }
    for (const s of slots) {
      if (s.day_of_week === "" || s.day_of_week === null || s.day_of_week === undefined) {
        toast({
          title: "Pick a day for every slot",
          status: "error",
          duration: 3000,
        });
        return;
      }
      if (!s.start_time || !s.end_time || s.end_time <= s.start_time) {
        toast({
          title: "Each slot needs a valid start and end time (end after start)",
          status: "error",
          duration: 3000,
        });
        return;
      }
      const startMin = parseInt(s.start_time.split(":")[1], 10);
      const endMin = parseInt(s.end_time.split(":")[1], 10);
      if (startMin % 15 !== 0 || endMin % 15 !== 0) {
        toast({
          title: "Times must be on 15-minute increments",
          status: "error",
          duration: 3000,
        });
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        rules: slots.map((s) => ({
          day_of_week: parseInt(s.day_of_week, 10),
          start_time: s.start_time,
          end_time: s.end_time,
        })),
      };
      const headers = await authHeaders();
      const res = await fetch(`${getBackendURL()}/api/meeting-availability/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrf(),
          ...headers,
        },
        body: JSON.stringify(payload),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || `HTTP ${res.status}`);
      toast({
        title: `Added ${slots.length} availability slot${slots.length === 1 ? "" : "s"}`,
        status: "success",
        duration: 3000,
      });
      setSlots([newSlot()]);
      await fetchRules();
    } catch (err) {
      toast({
        title: "Failed to save",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (rule) => {
    setEditingRuleId(rule.id);
    setEditingDraft({
      day_of_week: String(rule.day_of_week),
      start_time: (rule.start_time || "").slice(0, 5),
      end_time: (rule.end_time || "").slice(0, 5),
    });
  };

  const handleCancelEdit = () => {
    setEditingRuleId(null);
    setEditingDraft(null);
  };

  const handleEditChange = (field, value) =>
    setEditingDraft((prev) => ({ ...prev, [field]: value }));

  const handleSaveEdit = async () => {
    if (!editingDraft) return;
    const d = editingDraft;
    if (d.day_of_week === "" || d.day_of_week === null) {
      toast({ title: "Pick a day", status: "error", duration: 3000 });
      return;
    }
    if (!d.start_time || !d.end_time || d.end_time <= d.start_time) {
      toast({
        title: "End time must be after start time",
        status: "error",
        duration: 3000,
      });
      return;
    }
    const startMin = parseInt(d.start_time.split(":")[1], 10);
    const endMin = parseInt(d.end_time.split(":")[1], 10);
    if (startMin % 15 !== 0 || endMin % 15 !== 0) {
      toast({
        title: "Times must be on 15-minute increments",
        status: "error",
        duration: 3000,
      });
      return;
    }
    setSavingEdit(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(
        `${getBackendURL()}/api/meeting-availability/${editingRuleId}/`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrf(),
            ...headers,
          },
          body: JSON.stringify({
            day_of_week: parseInt(d.day_of_week, 10),
            start_time: d.start_time,
            end_time: d.end_time,
          }),
        },
      );
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || `HTTP ${res.status}`);
      if (j.rule) {
        setRules((prev) => prev.map((r) => (r.id === j.rule.id ? j.rule : r)));
      } else {
        await fetchRules();
      }
      setEditingRuleId(null);
      setEditingDraft(null);
      toast({ title: "Availability updated", status: "success", duration: 2500 });
    } catch (err) {
      toast({
        title: "Failed to update",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const headers = await authHeaders();
      const res = await fetch(
        `${getBackendURL()}/api/meeting-availability/${id}/`,
        {
          method: "DELETE",
          credentials: "include",
          headers: { "X-CSRFToken": csrf(), ...headers },
        },
      );
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || `HTTP ${res.status}`);
      setRules((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      toast({
        title: "Failed to delete",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Meeting Availability</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6} align="stretch">
            <Box>
              <Text fontWeight="bold" mb={2}>
                Existing availability
              </Text>
              <Text fontSize="sm" color="gray.600" mb={3}>
                Every team member can publish their own weekly availability.
                When someone books a slot in your window, you'll be added to
                the Zoho meeting and emailed the invite. You can only delete
                rows you own.
              </Text>
              {loading ? (
                <HStack>
                  <Spinner size="sm" />
                  <Text>Loading…</Text>
                </HStack>
              ) : rules.length === 0 ? (
                <Text color="gray.500">No availability configured yet.</Text>
              ) : (
                <Box overflowX="auto">
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Day</Th>
                        <Th>Time (ET)</Th>
                        <Th>Owner</Th>
                        <Th></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {rules.map((r) => {
                        const ownerLabel = r.owner_email
                          ? `${r.owner_name || r.owner_email}${
                              r.owner_name ? ` <${r.owner_email}>` : ""
                            }`
                          : "(unowned)";
                        const isEditing = editingRuleId === r.id && editingDraft;
                        if (isEditing) {
                          return (
                            <Tr key={r.id} bg="yellow.50">
                              <Td>
                                <Select
                                  size="sm"
                                  value={editingDraft.day_of_week}
                                  onChange={(e) =>
                                    handleEditChange("day_of_week", e.target.value)
                                  }
                                  isDisabled={savingEdit}
                                >
                                  {DAY_NAMES.map((name, i) => (
                                    <option key={i} value={i}>
                                      {name}
                                    </option>
                                  ))}
                                </Select>
                              </Td>
                              <Td>
                                <HStack spacing={1}>
                                  <Select
                                    size="sm"
                                    value={editingDraft.start_time}
                                    onChange={(e) =>
                                      handleEditChange("start_time", e.target.value)
                                    }
                                    isDisabled={savingEdit}
                                  >
                                    {TIME_OPTIONS.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </Select>
                                  <Text fontSize="xs">to</Text>
                                  <Select
                                    size="sm"
                                    value={editingDraft.end_time}
                                    onChange={(e) =>
                                      handleEditChange("end_time", e.target.value)
                                    }
                                    isDisabled={savingEdit}
                                  >
                                    {TIME_OPTIONS.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </Select>
                                </HStack>
                              </Td>
                              <Td>
                                <Text fontSize="sm">
                                  <strong>You</strong>
                                </Text>
                              </Td>
                              <Td>
                                <HStack spacing={1}>
                                  <IconButton
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="green"
                                    icon={<CheckIcon />}
                                    aria-label="Save edit"
                                    onClick={handleSaveEdit}
                                    isLoading={savingEdit}
                                  />
                                  <IconButton
                                    size="xs"
                                    variant="ghost"
                                    icon={<CloseIcon />}
                                    aria-label="Cancel edit"
                                    onClick={handleCancelEdit}
                                    isDisabled={savingEdit}
                                  />
                                </HStack>
                              </Td>
                            </Tr>
                          );
                        }
                        return (
                          <Tr
                            key={r.id}
                            bg={r.is_mine ? "blue.50" : undefined}
                          >
                            <Td>{DAY_NAMES[r.day_of_week]}</Td>
                            <Td>
                              {formatTime(r.start_time)} – {formatTime(r.end_time)} ET
                            </Td>
                            <Td>
                              <Text fontSize="sm">
                                {r.is_mine ? (
                                  <strong>You</strong>
                                ) : (
                                  ownerLabel
                                )}
                              </Text>
                            </Td>
                            <Td>
                              {r.is_mine ? (
                                <HStack spacing={1}>
                                  <IconButton
                                    size="xs"
                                    variant="ghost"
                                    icon={<EditIcon />}
                                    aria-label="Edit rule"
                                    onClick={() => handleStartEdit(r)}
                                    isDisabled={Boolean(editingRuleId)}
                                  />
                                  <IconButton
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="red"
                                    icon={<DeleteIcon />}
                                    aria-label="Delete rule"
                                    onClick={() => handleDelete(r.id)}
                                    isDisabled={Boolean(editingRuleId)}
                                  />
                                </HStack>
                              ) : null}
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </Box>

            <Box borderTop="1px solid" borderColor="gray.200" pt={4}>
              <Text fontWeight="bold" mb={3}>
                Add my availability
              </Text>
              <Text fontSize="sm" color="gray.600" mb={3}>
                Each row is a weekly recurring availability window — applies every week.
                Times are interpreted as <strong>Eastern Time</strong>. Rules
                you add here are owned by your account, and bookings inside
                them will email you.
              </Text>
              {!currentUserId && !loading ? (
                <Text fontSize="sm" color="red.500" mb={3}>
                  You must be signed in to add availability.
                </Text>
              ) : null}
              <VStack spacing={4} align="stretch">
                <Box>
                  <FormLabel mb={2}>Time slots (Eastern Time)</FormLabel>
                  <VStack spacing={2} align="stretch">
                    {slots.map((s, idx) => (
                      <HStack key={idx} spacing={2}>
                        <Select
                          value={s.day_of_week}
                          onChange={(e) =>
                            handleSlotChange(idx, "day_of_week", e.target.value)
                          }
                          placeholder="Select a day"
                          flex="0 0 150px"
                          isDisabled={saving}
                        >
                          {DAY_NAMES.map((name, i) => (
                            <option key={i} value={i}>
                              {name}
                            </option>
                          ))}
                        </Select>
                        <Select
                          value={s.start_time}
                          onChange={(e) =>
                            handleSlotChange(idx, "start_time", e.target.value)
                          }
                          flex="1"
                          isDisabled={saving}
                        >
                          {TIME_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Select>
                        <Text>to</Text>
                        <Select
                          value={s.end_time}
                          onChange={(e) =>
                            handleSlotChange(idx, "end_time", e.target.value)
                          }
                          flex="1"
                          isDisabled={saving}
                        >
                          {TIME_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Select>
                        <IconButton
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          icon={<DeleteIcon />}
                          aria-label="Remove slot"
                          onClick={() => handleRemoveSlot(idx)}
                          isDisabled={saving || slots.length === 1}
                        />
                      </HStack>
                    ))}
                  </VStack>
                  <Button
                    size="sm"
                    leftIcon={<AddIcon />}
                    variant="outline"
                    mt={2}
                    onClick={handleAddSlot}
                    isDisabled={saving}
                  >
                    Add another slot
                  </Button>
                </Box>
              </VStack>
            </Box>
          </VStack>
          <Divider my={6} />

          <Box>
            <FormLabel mb={1}>Your personal booking link</FormLabel>
            <Text fontSize="sm" color="gray.600" mb={3}>
              Share this link to let people book directly with you. It shows
              only your availability — not anyone else's.
            </Text>

            {!currentUserId ? (
              <Text fontSize="sm" color="gray.500">
                Sign in to generate a booking link.
              </Text>
            ) : !bookingSlug ? (
              <Button
                size="sm"
                colorScheme="blue"
                variant="outline"
                onClick={() => handleGenerateLink()}
                isLoading={linkBusy}
                loadingText="Generating"
              >
                Generate booking link
              </Button>
            ) : (
              <VStack align="stretch" spacing={2}>
                <HStack>
                  <Input
                    value={bookingUrl}
                    isReadOnly
                    fontSize="sm"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button size="sm" onClick={handleCopyLink} flexShrink={0}>
                    Copy
                  </Button>
                </HStack>
                <HStack justify="space-between">
                  <Button
                    as="a"
                    href={bookingUrl}
                    target="_blank"
                    rel="noreferrer"
                    size="xs"
                    variant="link"
                    colorScheme="blue"
                  >
                    Preview page
                  </Button>
                  <Button
                    size="xs"
                    variant="link"
                    colorScheme="red"
                    onClick={() => handleGenerateLink({ regenerate: true })}
                    isLoading={linkBusy}
                  >
                    Regenerate (breaks old link)
                  </Button>
                </HStack>
                {!rules.some((r) => r.is_mine) && (
                  <Text fontSize="xs" color="orange.600">
                    You have no availability saved yet — the page will show no
                    open times until you add some above.
                  </Text>
                )}
              </VStack>
            )}
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={saving}>
            Close
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={saving}
            loadingText="Saving"
            isDisabled={
              !currentUserId ||
              slots.some(
                (s) =>
                  s.day_of_week === "" ||
                  !s.start_time ||
                  !s.end_time ||
                  s.end_time <= s.start_time,
              )
            }
          >
            Save availability
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MeetingAvailabilityModal;
